import { inviteDestination, refreshSocialSummary } from "../net/social-actions.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Copy, Radio, Search, UserPlus } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Link, useNavigate } from "../app/navigation.js";
import type { InvitePresentation } from "@guandan/protocol";
import { Avatar } from "../art/Avatar.js";
import { api, ApiError, type FeedItem, type RelationshipKind, type SocialPlayer } from "../net/api.js";
import { useSession } from "../net/session.js";
import { usePlayerState } from "../app/ChannelProvider.js";
import { mergeRelationshipPage, pendingInvites, playerStore } from "../net/playerStore.js";
import { toWidePath } from "../app/widePath.js";
import { Button, EmptyState, PageHeader, Panel, Spinner } from "../ui/primitives.js";
import { useLocale } from "../i18n.js";
import "./friends.css";

/**
 * The friends panel.
 *
 * Following someone makes you their fan and puts their shared matches in your
 * feed. When they follow back you are friends, and friends — and only friends —
 * can invite each other to play.
 *
 * Nothing here polls. Invitations arrive on the player's channel and live in
 * the shared store, so the badge in the shell, this panel and any other tab the
 * player has open all show the same list and all update together. The panel
 * used to ask the server every five seconds and keep its own copy.
 */
type Tab = "feed" | RelationshipKind;
const TABS: Tab[] = ["feed", "friends", "following", "followers"];

export function Friends({ wide = false }: { wide?: boolean } = {}) {
  const { t, number, date, locale } = useLocale();
  const session = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab") as Tab) ? params.get("tab") as Tab : "feed";

  const invites = usePlayerState((state) => pendingInvites(state));
  const [feed, setFeed] = useState<FeedItem[] | null>(null);
  const [players, setPlayers] = useState<SocialPlayer[] | null>(null);
  const [relationshipCursor, setRelationshipCursor] = useState<string | null>(null);
  const [me, setMe] = useState<{ playerCode: string | null; friends: number; followers: number; following: number } | null>(null);
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<SocialPlayer[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const sentMap = usePlayerState((state) => state.sentInvitesById);
  const sent = Object.values(sentMap).filter((invite) => invite.state === "pending" && invite.expiresAt > Date.now());
  const [failed, setFailed] = useState(false);

  useEffect(() => { void api.socialMe().then(setMe).catch(() => undefined); }, []);

  const load = useCallback(async () => {
    setFailed(false);
    setPlayers(null);
    setFeed(null);
    setRelationshipCursor(null);
    if (tab === "feed") {
      const page = await api.feed().catch(() => { setFailed(true); return { items: [], cursor: null }; });
      setFeed(page.items);
      // The standings block is the following list; one indexed query rather
      // than a card fetch per followee.
      const following = await api.relationships("following").catch(() => { setFailed(true); return { players: [], cursor: null }; });
      setPlayers(following.players);
      setRelationshipCursor(null);
      playerStore.set((state) => mergeRelationshipPage(state, "following", following.players, following.cursor, false));
      return;
    }
    const page = await api.relationships(tab).catch(() => { setFailed(true); return { players: [], cursor: null }; });
    setPlayers(page.players);
    setRelationshipCursor(page.cursor);
    playerStore.set((state) => mergeRelationshipPage(state, tab, page.players, page.cursor, false));
  }, [tab]);
  useEffect(() => { void load(); }, [load]);

  async function loadMoreRelationships() {
    if (tab === "feed" || relationshipCursor === null || players === null) return;
    setBusy("load-more");
    try {
      const page = await api.relationships(tab, relationshipCursor);
      setPlayers((current) => [...(current ?? []), ...page.players]);
      setRelationshipCursor(page.cursor);
      playerStore.set((state) => mergeRelationshipPage(state, tab, page.players, page.cursor, true));
    } catch {
      setNote(t("somethingWrong"));
    } finally {
      setBusy(null);
    }
  }

  async function search(event: React.FormEvent) {
    event.preventDefault();
    const code = query.trim().toUpperCase();
    if (code.length < 4) return;
    try { setFound((await api.searchPlayer(code)).players); } catch { setNote(t("somethingWrong")); }
  }

  /** Optimistic, because the answer is never in doubt — only its timing. */
  async function toggleFollow(player: SocialPlayer, currentlyFollowing: boolean) {
    setBusy(player.userId);
    const patch = (list: SocialPlayer[] | null, bond: Partial<SocialPlayer>) =>
      list?.map((row) => (row.userId === player.userId ? { ...row, ...bond } : row)) ?? null;
    const optimistic = { following: !currentlyFollowing, friends: !currentlyFollowing && player.followsMe === true };
    setFound((list) => patch(list, optimistic));
    setPlayers((list) => patch(list, optimistic));
    try {
      const bond = currentlyFollowing ? await api.unfollow(player.userId) : await api.follow(player.userId);
      setFound((list) => patch(list, bond));
      setPlayers((list) => patch(list, bond));
      setMe(await api.socialMe().catch(() => me));
      await load();
    } catch (error) {
      // Put it back. The row was never allowed to lie for longer than the
      // request took.
      setFound((list) => patch(list, { following: currentlyFollowing, friends: player.friends }));
      setPlayers((list) => patch(list, { following: currentlyFollowing, friends: player.friends }));
      setNote(error instanceof ApiError && error.code === "follow_limit_reached"
        ? t("followLimitReached") : t("somethingWrong"));
    } finally {
      setBusy(null);
    }
  }

  /**
   * One idempotency key per user action, held for as long as the action lasts.
   *
   * A double tap, or a retry after a dropped response, resolves to the single
   * invitation that already exists rather than sending another.
   */
  const inviteKeys = useRef(new Map<string, string>());

  async function invite(player: SocialPlayer, kind: "duo" | "gold") {
    setBusy(player.userId);
    const keyId = `${player.userId}:${kind}`;
    const key = inviteKeys.current.get(keyId) ?? crypto.randomUUID();
    inviteKeys.current.set(keyId, key);
    try {
      const destination = await inviteDestination(kind);
      const result = await api.invite(player.userId, kind, destination.code, key);
      playerStore.set((state) => ({ ...state, sentInvitesById: { ...state.sentInvitesById, [result.invite.id]: result.invite } }));
      inviteKeys.current.delete(keyId);
      setNote(t("inviteSent"));
      void refreshSocialSummary().catch(() => undefined);
      window.setTimeout(() => setNote(null), 2000);
    } catch (error) {
      // The key is kept, so pressing the button again reuses it rather than
      // sending a second invitation.
      //
      // Inviting means creating a room to invite someone *into*, so the common
      // failure is not the invitation at all — it is that this player is
      // already in one. Saying so beats a generic apology the player cannot act
      // on.
      const code = error instanceof ApiError ? error.code : "";
      setNote(
        code === "already_in_lobby" ? t("alreadyInLobby")
          : code === "already_seated" ? t("alreadySeated")
            : code === "insufficient-balance-or-level" ? t("notEnoughStake")
              : t("somethingWrong"),
      );
      window.setTimeout(() => setNote(null), 4000);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Keep the player in the layout they are already in.
   *
   * The wide shell lives under `/w`, and only a handful of paths — a table, a
   * room — are treated as wide by viewport alone. A party link without the
   * prefix would drop a desktop player into the mobile shell mid-flow.
   */
  const go = useCallback((to: string) => navigate(wide ? toWidePath(to) : to), [navigate, wide]);

  /**
   * Accept, then go where the server says.
   *
   * The old panel linked straight to `/room/<code>` from the invitation
   * payload, which meant a player could walk into a lobby that had filled up,
   * expired, or that they could not afford — and the server never learned the
   * invitation had been taken. The destination now comes back from `/accept`.
   */
  async function accept(invite: InvitePresentation) {
    setBusy(invite.id);
    try {
      const result = await api.acceptInvite(invite.id);
      void refreshSocialSummary().catch(() => undefined);
      go(result.destination.type === "party" ? `/party/${result.destination.id}` : `/room/${result.destination.code}`);
    } catch (error) {
      const code = error instanceof ApiError ? error.code : "";
      setNote(
        code === "invite_expired" ? t("inviteExpired")
          : code === "party_full" ? t("partyFull")
            : code === "invite_already_handled" ? t("inviteAlreadyHandled")
              : t("somethingWrong"),
      );
    } finally {
      setBusy(null);
    }
  }

  if (session.status !== "signedIn") return <Spinner label={t("loading")} />;

  return (
    <>
      {sent.length > 0 && <Panel className="invite-panel"><h2 className="section-title">{locale === "zh-CN" ? "已发出的邀请" : "Sent invitations"}</h2>{sent.map((invite) => <div className="invite-row" key={invite.id}>
        <span>{locale === "zh-CN" ? "等待好友加入" : "Waiting for your friend"} · {invite.destinationCode}</span>
        <Button size="sm" variant="ghost" busy={busy === invite.id} onClick={() => {
          setBusy(invite.id);
          void api.cancelInvite(invite.id).then(() => playerStore.set((state) => ({ ...state, sentInvitesById: { ...state.sentInvitesById, [invite.id]: { ...invite, state: "cancelled" } } }))).catch(() => setNote(t("somethingWrong"))).finally(() => setBusy(null));
        }}>{locale === "zh-CN" ? "撤销邀请" : "Cancel invitation"}</Button>
      </div>)}</Panel>}
      {invites.length > 0 && (
        <Panel className="invite-panel">
          {invites.map((item) => (
            // Keyed by the invitation, not the sender: one friend can have more
            // than one invitation outstanding, and the old key collapsed them.
            <div className="invite-row" key={item.id}>
              <span>{t("inviteFrom", { name: item.senderName })}</span>
              <div className="invite-actions">
                <Button busy={busy === item.id} onClick={() => void accept(item)}>{t("accept")}</Button>
                <Button variant="ghost" onClick={() => void api.declineInvite(item.id).then(refreshSocialSummary).catch(() => setNote(t("somethingWrong")))}>{t("decline")}</Button>
              </div>
            </div>
          ))}
        </Panel>
      )}

      {wide ? (
        <PageHeader
          title={t("friends")}
          action={me?.playerCode != null ? (
            <div className="my-code">
              <span className="field-label">{t("yourCode")}</span>
              <span>
                <strong className="tabular">{me.playerCode}</strong>
                <button type="button" className="social-code-copy" onClick={() => void navigator.clipboard.writeText(me.playerCode!)} aria-label={locale === "zh-CN" ? "复制" : "Copy"} title={locale === "zh-CN" ? "复制" : "Copy"}><Copy aria-hidden="true" /></button>
              </span>
            </div>
          ) : undefined}
        />
      ) : (
        <Panel className="social-head">
          <div>
            <h2 className="section-title">{t("friends")}</h2>
            <p className="section-hint">{t("playerCodeHint")}</p>
          </div>
          {me?.playerCode != null && (
            <div className="my-code">
              <span className="field-label">{t("yourCode")}</span>
              <strong className="tabular">{me.playerCode}</strong>
            </div>
          )}
        </Panel>
      )}

      <Panel>
        <form className="social-search-form" onSubmit={search}>
          <input
            className="field-input social-search-input tabular"
            value={query}
            onChange={(e) => setQuery(e.target.value.toUpperCase())}
            placeholder={t("playerCode")}
            maxLength={6}
            aria-label={t("findPlayer")}
          />
          <Button type="submit" disabled={query.trim().length < 4}><Search size={16} aria-hidden="true" />{t("findPlayer")}</Button>
        </form>
        {found !== null && (found.length === 0
          ? <p className="section-hint">{t("noSuchPlayer")}</p>
          : found.map((player) => (
            <PlayerRow key={player.userId} player={player} busy={busy === player.userId}
              onFollow={toggleFollow} onInvite={invite} t={t} number={number} />
          )))}
      </Panel>

      <nav className="profile-tabs wide-social-tabs" role="tablist" aria-label={t("friends")}>
        {TABS.map((item) => (
          <button key={item} type="button" role="tab" aria-selected={tab === item}
            className={tab === item ? "profile-tab profile-tab-active" : "profile-tab"}
            onClick={() => setParams(item === "feed" ? {} : { tab: item })}>
            {t(item === "feed" ? "feed" : item)}
          </button>
        ))}
      </nav>

      {note !== null && <p className="success-note" role="status">{note}</p>}

      {failed ? <EmptyState title={t("couldNotLoad")} action={<Button onClick={() => void load()}>{t("retry")}</Button>} /> : tab === "feed"
        ? <Feed feed={feed} players={players} t={t} number={number} date={date} />
        : players === null
          ? <Spinner label={t("loading")} />
          : players.length === 0
            ? <EmptyState icon={<UserPlus />} title={t("noFriends")} hint={t("noFriendsHint")} />
            : (
              <>
                <Panel>
                  {players.map((player) => (
                    <PlayerRow key={player.userId} player={player} busy={busy === player.userId}
                      onFollow={toggleFollow} onInvite={invite} t={t} number={number} />
                  ))}
                </Panel>
                {relationshipCursor !== null && (
                  <Button variant="ghost" busy={busy === "load-more"} onClick={() => void loadMoreRelationships()}>
                    {t("more")}
                  </Button>
                )}
              </>
            )}
    </>
  );
}

function PlayerRow({ player, busy, onFollow, onInvite, t, number }: {
  player: SocialPlayer;
  busy: boolean;
  onFollow: (player: SocialPlayer, following: boolean) => void;
  onInvite: (player: SocialPlayer, kind: "duo" | "gold") => void;
  t: ReturnType<typeof useLocale>["t"];
  number: (value: number) => string;
}) {
  const following = player.following ?? true;
  // A hint only. Whether the invitation can actually be taken is decided by the
  // server when they tap accept, never by a dot that was true a moment ago.
  const away = player.presence === "inMatch" || player.presence === "inLobby";
  return (
    <div className="social-row">
      <Avatar avatarId={player.avatarId} size={40} />
      <div className="social-id">
        <strong className="social-name">{player.name}</strong>
        <span className="social-sub tabular">
          {number(player.rating)} {t("rating")} · {Math.round(player.winRate * 100)}% {t("winRate")}
          {player.friends === true && <em className="social-badge">{t("mutual")}</em>}
          {player.friends !== true && player.followsMe === true && <em className="social-badge">{t("followsYou")}</em>}
          {player.presence === "online" && <em className="social-badge social-online">{t("online")}</em>}
        </span>
      </div>
      <div className="social-actions">
        {player.friends === true && (
          <>
            <Button variant="secondary" busy={busy} disabled={away} onClick={() => onInvite(player, "duo")}>{t("inviteToDuo")}</Button>
            <Button variant="ghost" busy={busy} disabled={away} onClick={() => onInvite(player, "gold")}>{t("inviteToGold")}</Button>
          </>
        )}
        {player.isMe !== true && (
          <Button variant={following ? "ghost" : "primary"} busy={busy} onClick={() => onFollow(player, following)}>
            {following ? t("unfollow") : t("follow")}
          </Button>
        )}
      </div>
    </div>
  );
}

interface SharePayload {
  roomId: string;
  slug: string;
  card: { winner: number; hands: number } | null;
}

/** What the people I follow have been doing: where they stand, and what they shared. */
function Feed({ feed, players, t, number, date }: {
  feed: FeedItem[] | null;
  players: SocialPlayer[] | null;
  t: ReturnType<typeof useLocale>["t"];
  number: (value: number) => string;
  date: (value: number) => string;
}) {
  if (feed === null || players === null) return <Spinner label={t("loading")} />;
  if (players.length === 0) return <EmptyState icon={<Radio />} title={t("noFeed")} hint={t("noFeedHint")} />;
  const shares = feed.filter((item) => item.type === "replay.shared");
  return (
    <>
      <Panel>
        <h2 className="section-title">{t("following")}</h2>
        {players.map((player) => (
          <div className="social-row" key={player.userId}>
            <Avatar avatarId={player.avatarId} size={36} />
            <div className="social-id">
              <strong className="social-name">{player.name}</strong>
              <span className="social-sub tabular">
                {number(player.rating)} {t("rating")} · {Math.round(player.winRate * 100)}% {t("winRate")} · {number(player.matches)} {t("matches")}
              </span>
            </div>
          </div>
        ))}
      </Panel>
      <Panel>
        <h2 className="section-title">{t("sharedReplays")}</h2>
        {shares.length === 0
          ? <p className="section-hint">{t("noFeedHint")}</p>
          : (
            <ol className="replay-list">
              {shares.map((item) => {
                const share = item.payload as SharePayload;
                return (
                  <li className="replay-row" key={item.id}>
                    <Link className="replay-open" to={`/r/${share.slug}`}>
                      <span className="replay-meta">
                        <span className="replay-date">
                          {share.card === null
                            ? t("watchReplay")
                            : t("replayOf", { winner: share.card.winner === 0 ? t("teamA") : t("teamB"), hands: number(share.card.hands) })}
                        </span>
                        <span className="replay-sub">{date(item.occurredAt)}</span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ol>
          )}
      </Panel>
    </>
  );
}
